import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NavigationService } from '../../navigation.service';
import { Router, Event, NavigationEnd } from '@angular/router';
import { AuthService } from '@shared/services/auth/auth.service';
import { MatDialog } from '@angular/material/dialog';
import { HeaderProfilePopupComponent } from '../header-profile-popup/header-profile-popup.component';
import { OrgSelectObject, UserService } from '@app/admin-station/work-center/user/services/user.service';
import { SideNavComponent } from '@app/shared/components/side-nav/side-nav.component';
import { UtilService } from '@app/shared/services/utils/util.service';
import { DrawerService } from '@shared/components/drawer/drawer.service';
import { HeaderNotificationsComponent } from '@app/main/components/header-notifications/header-notifications.component';
import { NotificationService } from '@shared/services/notification.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HeaderFeedbackComponent } from '../header-feedback/header-feedback.component';
import { distinctUntilChanged } from 'rxjs';
import { ImageService } from '@app/shared/services/images/image.service';
import { mobileFooterNavItems } from '@app/main/navigation-items';
import { User } from '@app/shared/models/user.model';
import { InputDensityEnum } from '@app/shared/enums/form-field';

@Component({
  selector: 'cm-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss', '../../../styles/base/_typography.scss'],
})
@UntilDestroy()
export class HeaderComponent implements OnInit {
  @Input() searchItem = '';
  @Output() searchSubmit: EventEmitter<string>;
  @ViewChild('container', { read: ViewContainerRef })
  viewContainerRef: ViewContainerRef;
  @ViewChild('cmSideNav') cmSideNavRef: SideNavComponent;

  public authUrl: string;
  public fallbackUrl: string;
  public switchIcon: boolean;
  public userName = '';
  public userId = '';
  public userInfo: Partial<User>;
  public userFullName = '';
  public routeUrl: string;
  public helpPopoverClick: boolean;
  public profilePopoverClick: boolean;
  public mobile = false;
  public imageUrlLoading = false;
  public mobileSideNavStep = 1;
  public notificationsCount$ = this._notificationService.notificationCounter$;

  imagePath = '';
  mobileFooterNavItems = mobileFooterNavItems;

  InputDensityEnum = InputDensityEnum;
  selectedOrg = null;

  constructor(
    public userService: UserService,
    private _authService: AuthService,
    private _dialog: MatDialog,
    private _drawerService: DrawerService,
    private _imageService: ImageService,
    private _navigationService: NavigationService,
    private _notificationService: NotificationService,
    private _router: Router
  ) {
    this.helpPopoverClick = false;
    this.profilePopoverClick = false;
    this.onResize();

    this.userService.userLoginData$
      .pipe(
        distinctUntilChanged(),
        untilDestroyed(this)
      )
      .subscribe((userData) => {
        if (!userData) return;
        this.userId = userData.userId;
        this.userFullName = userData.fullName;
        this._loadUserInfo();
        this.selectedOrg = this.userService.selectedOrg
      });
  }

  @HostListener('window:resize', ['$event']) onResize(): void {
    this.mobile = UtilService.isMobileAndTabletSize();
  }

  rootOrgSelected(selectedOrg: any) {
    if (selectedOrg?.value) {
      this.userService.changeRootOrg(selectedOrg.value);
    }
  }

  ngOnInit(): void {
    this._router.events.subscribe((event: Event) => {
      if (event instanceof NavigationEnd) {
        this.routeUrl = this._router.url;
      }
    });

    this._navigationService.sidenavState.subscribe((state) => {
      this.switchIcon = state;
    });
  }

  toggleNav(): void {
    this._navigationService.sidenavState.next(!this.switchIcon);
  }

  openMovilSideProfilePicPanel(): void {
    this.cmSideNavRef.open();
  }

  openImageCroppingMobile(): void {
    this.mobileSideNavStep = 2;
  }

  setInitSideNav(): void {
    this.mobileSideNavStep = 1;
  }

  closeSideNav(): void {
    this.cmSideNavRef.close();
    this._loadUserInfo();
  }

  private _loadUserInfo(): void {
    this.imageUrlLoading = true;
    this.userService.getUserById(this.userId).then((userInfo) => {
      this.userInfo = userInfo;
      this._imageService.getAuthenticatedUrl(userInfo?.imageUrl).subscribe((authUrl) => {
        this.authUrl = authUrl.authUrl;
        this.fallbackUrl = authUrl.fallbackUrl;
        this.imageUrlLoading = false;
      });
    });
  }

  logout() {
    this._authService.logout();
  }

  closeImageCroppingMobile() {
    this.imageUrlLoading = true;
    setTimeout(() => {
      this._loadUserInfo();
    }, 1000);
  }

  closePopOver(popOverName: string) {
    switch (popOverName) {
      case 'profile':
        this.profilePopoverClick = false;
        break;
      case 'help':
        this.helpPopoverClick = false;
        break;
    }
  }

  openPopOver(popOverName: string) {
    switch (popOverName) {
      case 'profile':
        this.profilePopoverClick = true;
        break;
      case 'help':
        this.helpPopoverClick = true;
        break;
    }
  }

  openNotifications(): void {
    this._drawerService.setDrawerConfiguration({
      title: 'notifications.title',
      cancelButton: {
        label: 'string.cancel',
        clickHandler: () => this._drawerService.close(),
      },
      primaryButton: {
        label: 'notifications.view-all',
        clickHandler: () =>
          this._router.navigate(['/notifications-center/activity']).then(() => this._drawerService.close()),
      },
      width: '24%',
    });
    this._drawerService.open(HeaderNotificationsComponent);
  }

  public openFeedbackDialog(): void {
    this._dialog.open(HeaderFeedbackComponent, {
      autoFocus: false,
      restoreFocus: false,
    });
  }

  onMobileProfileClick() {
    this._dialog.open(HeaderProfilePopupComponent, {
      panelClass: 'cm-header-pop-up',
      width: '100%',
      maxWidth: '334px',
      minHeight: '237px',
    });
  }

  handleImageError(event: any) {
    if (event.target.src?.includes('resizedimages')) {
      this.authUrl = this.fallbackUrl;
    } else {
      this.authUrl = null;
    }
  }
}
